import { useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ComplianceResult {
  gravamen_porcentaje: number;
  iva_porcentaje: number;
  descripcion_arancel: string | null;
  matched_codigo?: string;
  regla?: {
    entidad: string;
    tipo_tramite: string;
    descripcion_regla: string;
    nivel_riesgo: string;
    nota_marginal_exclusiones: string;
    norma: string;
  };
}

/**
 * Lookup state per key:
 *   - "loading"  → query in flight
 *   - "matched"  → arancel found (result populated)
 *   - "unknown"  → query completed, NO match in arancel_1881 (silent failure surfaced)
 */
export type ComplianceStatus = "loading" | "matched" | "unknown";

/** Sanitize HS code: keep only digits */
export function sanitizeHsCode(raw: string): string {
  return raw.replace(/\D/g, "").trim();
}

/** Format a clean HS code as XXXX.XX.XX.XX (always padded to 10 digits) */
export function formatHsCode(raw: string): string {
  const clean = sanitizeHsCode(raw);
  if (!clean) return "";
  const padded = clean.length < 10 ? clean.padEnd(10, "0") : clean;
  const parts: string[] = [];
  if (padded.length > 0) parts.push(padded.slice(0, 4));
  if (padded.length > 4) parts.push(padded.slice(4, 6));
  if (padded.length > 6) parts.push(padded.slice(6, 8));
  if (padded.length > 8) parts.push(padded.slice(8, 10));
  return parts.join(".");
}

/**
 * Pure lookup engine — performs the cascade query against arancel_1881
 * and reglas_cumplimiento. Returns null on no-match.
 * Extracted so both the legacy index-based API and the new key-based API
 * share the same network logic.
 */
async function performLookup(rawCodigo: string): Promise<ComplianceResult | null> {
  const clean = rawCodigo.replace(/\D/g, "").trim();
  if (!clean) return null;

  const padded = clean.length < 10 ? clean.padEnd(10, "0") : clean;

  // --- Attempt 1: exact match (padded to 10) ---
  const candidates = [padded];
  if (padded.length > 10) candidates.push(padded.slice(0, 10));

  let arancel: any = null;
  let matchedCode = padded;
  for (const candidate of candidates) {
    const { data } = await supabase
      .from("arancel_1881")
      .select("*")
      .eq("codigo_partida", candidate)
      .maybeSingle();
    if (data) {
      arancel = data;
      matchedCode = candidate;
      break;
    }
  }

  // --- Attempt 2: prefix LIKE search (6-digit root) ---
  if (!arancel && clean.length >= 4) {
    const prefix = clean.substring(0, Math.min(clean.length, 6));
    const { data } = await supabase
      .from("arancel_1881")
      .select("*")
      .like("codigo_partida", `${prefix}%`)
      .limit(1)
      .maybeSingle();
    if (data) {
      arancel = data;
      matchedCode = data.codigo_partida;
    }
  }

  if (!arancel) return null;

  const result: ComplianceResult = {
    gravamen_porcentaje: Number(arancel.gravamen_porcentaje),
    iva_porcentaje: Number(arancel.iva_porcentaje),
    descripcion_arancel: arancel.descripcion,
    matched_codigo: matchedCode,
  };

  // Regla lookup using matched code
  const { data: regla } = await supabase
    .from("reglas_cumplimiento")
    .select("*")
    .eq("codigo_partida", matchedCode)
    .maybeSingle();

  if (regla) {
    result.regla = {
      entidad: regla.entidad ?? "",
      tipo_tramite: regla.tipo_tramite ?? "",
      descripcion_regla: regla.descripcion_regla ?? "",
      nivel_riesgo: regla.nivel_riesgo ?? "",
      nota_marginal_exclusiones: regla.nota_marginal_exclusiones ?? "",
      norma: regla.norma ?? "",
    };
  }

  return result;
}

/**
 * Reactive compliance lookup.
 *
 * Exposes TWO parallel APIs sharing the same HS-code cache:
 *
 *  1. Legacy index-based (kept for backward compat with AuditPanel,
 *     DashboardKPIs, EmbarqueDetailModal, RiskRadar):
 *       - `lookup(idx, code)` and `results: Record<number, ComplianceResult>`
 *
 *  2. NEW key-based (B-04 fix — recommended for any new caller, immune to
 *     array re-ordering / filtering bugs):
 *       - `lookupByKey(key, code)` and `resultsByKey: Record<string, ComplianceResult>`
 *       - `statusByKey: Record<string, ComplianceStatus>` to differentiate
 *         "loading" vs "unknown HS code" vs "matched" (Fix R3 — no more silent
 *         failures when the HS code is not in arancel_1881).
 *
 * Cache is keyed by sanitized HS code so both APIs benefit from the same
 * dedupe — calling lookup(0, "8471.30…") and lookupByKey("item-uuid", "8471.30…")
 * triggers a single network round-trip.
 */
export function useComplianceLookup() {
  const [results, setResults] = useState<Record<number, ComplianceResult>>({});
  const [resultsByKey, setResultsByKey] = useState<Record<string, ComplianceResult>>({});
  const [statusByKey, setStatusByKey] = useState<Record<string, ComplianceStatus>>({});
  const cache = useRef<Record<string, ComplianceResult | null>>({});

  // ── Legacy index-based API ───────────────────────────────────────────
  const lookup = useCallback(async (idx: number, rawCodigo: string) => {
    const clean = rawCodigo.replace(/\D/g, "").trim();
    if (!clean) {
      setResults((prev) => {
        const next = { ...prev };
        delete next[idx];
        return next;
      });
      return;
    }

    if (clean in cache.current) {
      const cached = cache.current[clean];
      if (cached) {
        setResults((prev) => ({ ...prev, [idx]: cached }));
      } else {
        setResults((prev) => {
          const next = { ...prev };
          delete next[idx];
          return next;
        });
      }
      return;
    }

    const result = await performLookup(clean);
    cache.current[clean] = result;
    if (!result) {
      setResults((prev) => {
        const next = { ...prev };
        delete next[idx];
        return next;
      });
      return;
    }
    setResults((prev) => ({ ...prev, [idx]: result }));
  }, []);

  // ── NEW key-based API (B-04) ─────────────────────────────────────────
  const lookupByKey = useCallback(async (key: string, rawCodigo: string) => {
    if (!key) return;
    const clean = rawCodigo.replace(/\D/g, "").trim();

    if (!clean) {
      setResultsByKey((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setStatusByKey((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }

    // Cache hit — resolve synchronously, no loading flicker.
    if (clean in cache.current) {
      const cached = cache.current[clean];
      if (cached) {
        setResultsByKey((prev) => ({ ...prev, [key]: cached }));
        setStatusByKey((prev) => ({ ...prev, [key]: "matched" }));
      } else {
        setResultsByKey((prev) => {
          if (!(key in prev)) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
        setStatusByKey((prev) => ({ ...prev, [key]: "unknown" }));
      }
      return;
    }

    // Mark loading before network round-trip.
    setStatusByKey((prev) => ({ ...prev, [key]: "loading" }));

    const result = await performLookup(clean);
    cache.current[clean] = result;

    if (!result) {
      setResultsByKey((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setStatusByKey((prev) => ({ ...prev, [key]: "unknown" }));
      return;
    }

    setResultsByKey((prev) => ({ ...prev, [key]: result }));
    setStatusByKey((prev) => ({ ...prev, [key]: "matched" }));
  }, []);

  const clearAll = useCallback(() => {
    setResults({});
    setResultsByKey({});
    setStatusByKey({});
    cache.current = {};
  }, []);

  return { results, lookup, resultsByKey, statusByKey, lookupByKey, clearAll };
}
